import { transformPdpProduct } from "./transformers/PdpPageProductDataTransformer";
import { type PdpPageProductDataDto } from "./dto/PdpPageProductDataDto";
import { DesktopProductDetailsPage } from "./components/DesktopProductDetailsPage";

import { MobileProductDetailsPage } from "./components/MobileProductDetailsPage";
import useMediaQuery from "../common/hooks/useMediaQuery";
import { useOnMount } from "../common/hooks/useOnMount";
import { loadPDPData } from "./redux/actions";
import { useDispatch } from "react-redux";
import { useFetch } from "../common/hooks/useFetch";
import { fetchPreferredProducts } from "./api/fetchPreferredProducts";
import { addMessage } from "../redux/globalMessages/actions";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../utils/screenSizes";
import { RecentlyViewedProducts } from "~/common/components/RecentlyViewedProducts/RecentlyViewedProducts";
import { useTranslation } from "react-i18next";
import { type TechnicalInfoDataDTO } from "~/Search/dto/SearchResultsDTO";
import { useLayoutEffect, useState } from "react";
import { trackPdpView } from "~/feature/Analytics/ga/abandonment";
import last from "lodash/last";
import { useRelatedProducts } from "~/redux/products/relatedProducts/useRelatedProducts";
import { addBaseProductDataAction } from "~/redux/products/actions";
import { useFrequentlyBoughtTogetherProducts } from "~/ProductDetails/components/FrequentlyBoughtTogetherSection/useFrequentlyBoughtTogetherProducts";
import type {
  PdpPageProductData,
  PreferredProductData,
  RelatedProductData,
} from "~/ProductDetails/models/PdpPageProductData";
import type { LoadableEntry } from "~/redux/loadableStore/types";
import type { SrpProductData } from "~/utils/models";
import { FrequentlyBoughtTogetherSection } from "~/ProductDetails/components/FrequentlyBoughtTogetherSection/FrequentlyBoughtTogetherSection";
import { PurchasePlaceProvider } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import PreferredProductsBlock from "~/ProductDetails/components/PreferredProducts/PreferredProductsBlock";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import RelatedProductsBlock from "~/ProductDetails/components/RelatedProducts/RelatedProductsBlock";
import { PDP_RESTRICTION_URL_PARAM } from "~/common/components/PackageRestrictionContext/utils/constants";
import { useCalculateRestrictedCode } from "~/common/components/PackageRestrictionContext/useCalculateRestrictedCode";
import { PackageRestrictionContext } from "~/common/components/PackageRestrictionContext/PackageRestrictionContext";
import { type AddToCartFormData, useAddToCartForm } from "~/AddToCart/hooks/useAddToCartNew";
import { makeSalesAwarePackCodeProvider } from "~/SalesCampaign/salesAwarePackCodeProvider";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { FormProvider, type UseFormReturn } from "react-hook-form";

interface Props {
  product: PdpPageProductData;
  showFBT: boolean;
}

export interface CommonProductDetailsPageProps {
  productCode: string;
  designation: string;
  relatedProducts: RelatedProductData[];
  relatedProductsLoading: boolean;
  preferredProducts: LoadableEntry<{
    preferred: PreferredProductData[];
    sales: SrpProductData[];
  }>;
}

type InnerProductDetailsPageProps = CommonProductDetailsPageProps & {
  showFBT: boolean;
};

const InnerProductDetailsPage = ({ showFBT, ...fullProps }: InnerProductDetailsPageProps) => {
  const params = new URLSearchParams(window.location.search);
  const query = params.get(PDP_RESTRICTION_URL_PARAM) ?? "";
  const restrictedCode = useCalculateRestrictedCode(query, fullProps.productCode);

  const [formProps, productDescriptor] = useAddToCartForm({
    productCode: fullProps.productCode,
    initialPackCodeProvider: makeSalesAwarePackCodeProvider(restrictedCode),
  }) as [UseFormReturn<AddToCartFormData>, ProductDescriptor<PdpPageProductData>];

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  const fbtProps = useFrequentlyBoughtTogetherProducts(fullProps.productCode, showFBT);
  const postSummarySlot = fbtProps.showFBTComponent && (
    <FrequentlyBoughtTogetherSection
      className={"scha__pdp__frequently-bought-together"}
      loading={fbtProps.loadingFBT}
      mainProduct={fbtProps.mainProduct}
      firstProduct={fbtProps.firstProduct}
      secondProduct={fbtProps.secondProduct}
    />
  );

  const finalProps = {
    ...fullProps,
    postSummarySlot,
    productDescriptor,
  };

  const page = isDesktop ? <DesktopProductDetailsPage {...finalProps} /> : <MobileProductDetailsPage {...finalProps} />;

  const { relatedProducts, relatedProductsLoading, preferredProducts } = fullProps;
  const product = productDescriptor.base;

  return (
    <FormProvider {...formProps}>
      <PackageRestrictionContext value={restrictedCode}>{page}</PackageRestrictionContext>
      {preferredProducts.data && (
        <PreferredProductsBlock
          productFamilyName={product.preferredProductsFamilyName}
          preferredProducts={preferredProducts}
        />
      )}

      {preferredProducts.state !== "loading" && relatedProductsLoading && (
        <div className="scha__related-products">
          <CircleSpinner />
        </div>
      )}
      {relatedProducts && (
        <RelatedProductsBlock relatedProducts={relatedProducts} mainProductDesignation={product.designation} />
      )}
      <RecentlyViewedProducts variant={"pdp"} />
    </FormProvider>
  );
};

export const ProductDetailsPage = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const product = props.product;
  const productCode = product.code;
  const [loaded, setLoaded] = useState(false);

  useOnMount(() => {
    dispatch(loadPDPData(product));

    trackPdpView(product, last(product.productAllCategories) ?? "");

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("redirect") !== "true") {
      if (urlParams.get("isPartialMatch") === "true") {
        dispatch(addMessage("info", t("page.pdp.toast.partialMatch")));
      }
      if (urlParams.get("isNewerVersion") === "true") {
        dispatch(addMessage("info", t("page.pdp.toast.newerVersion")));
      }
    }
    setLoaded(true);
  });

  const preferredProducts = useFetch(fetchPreferredProducts, productCode);
  const { relatedProducts, state } = useRelatedProducts(productCode);
  const relatedProductsLoading = state === "loading";

  useLayoutEffect(() => {
    if (preferredProducts.state === "loaded") {
      dispatch(addBaseProductDataAction(preferredProducts.data?.sales ?? []));
    }
  }, [preferredProducts]);

  if (!loaded) {
    return null;
  }

  const fullProps: InnerProductDetailsPageProps = {
    productCode,
    designation: props.product.designation,
    preferredProducts,
    relatedProducts,
    relatedProductsLoading,
    showFBT: props.showFBT,
  };

  return <InnerProductDetailsPage {...fullProps} />;
};

interface ProductDetailsPageData {
  // ProductDataDTO as string
  product: string;
  // Tech
  technicalInfo: string;
  // Toggle for frequently bought together product
  showFBT: boolean;
}

export function createProductDetailsPage(data: ProductDetailsPageData) {
  const dto = JSON.parse(data.product) as PdpPageProductDataDto;
  const techInfo = JSON.parse(data.technicalInfo) as TechnicalInfoDataDTO;

  const product = transformPdpProduct(dto, techInfo);

  return (
    <PurchasePlaceProvider value={"PDP"}>
      <ProductDetailsPage product={product} showFBT={data.showFBT} />
    </PurchasePlaceProvider>
  );
}
